import { DataAccessSearchItem } from '@sonar-software/sonar-core';
import { debounce } from 'lodash';
import { DynamicFilter } from '@sonar-software/sonar-ts-types';
import { Flex } from 'antd';
import { getFilterId } from '../@arch/lib';
import { LoadingSpinner } from '@ui/LoadingSpinner';

import { COPY_SPECIFIC, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { FilterComponent, FilterComponentBaseProps, FilterComponentProps } from './FilterComponent';
import React, { useMemo, useRef, useState } from 'react';
import Select, { DefaultOptionType } from 'antd/es/select';

export type LookAheadFilterOption = { label: string; value: string; };

export type LookAheadFilterComponent = React.FC<FilterComponentProps<Array<LookAheadFilterOption>>>;

type LookAheadFilterBaseProps = {
	endpoint: ({ queryString }: { queryString: string }) => Promise<Array<DataAccessSearchItem>>;
} & FilterComponentBaseProps<Array<LookAheadFilterOption>>;

const noOption = (label: string | undefined) => ({
	label: COPY_SPECIFIC.NO_LABELED(label),
	value: 'null'
});

const MIN_SEARCH_QUERY_LENGTH = 3;

export const LookAheadFilter: React.FC<LookAheadFilterBaseProps> = (props) => {
	const { endpoint, format, id, label, onChange, placeholder, value } = props;

	const [ isLoading, setIsLoading ] = useState(false);
	const [ options, setOptions ] = useState<Array<DefaultOptionType>>([]);

	const [ search, setSearch ] = useState<string>('');
	const lastSearched = useRef<string>('');

	const valueWithNull = useMemo(() =>
		(value && value.find((item) => item.value === 'null' && item.label === '') ?
			[
				...value.filter((item) => item.value !== 'null' || item.label !== ''),
				noOption(label)
			] : value) ?? [], [ label, value ]);

	const getSelectOptions = useMemo(() => debounce(async(searchValue: string | undefined) => {
		setSearch(searchValue ?? '');

		if (searchValue && searchValue.length >= MIN_SEARCH_QUERY_LENGTH && lastSearched.current !== searchValue) {
			lastSearched.current = searchValue;
			setIsLoading(true);

			const result = await endpoint({ queryString: searchValue });

			if (result) {
				setOptions([
					noOption(label),
					...result.map((item) => ({
						label: item.display,
						value: item.id
					}))
				]);
			}

			setIsLoading(false);
		}
	}, 300), [ endpoint, label ]);

	let emptyContent = undefined;

	if (isLoading) {
		emptyContent = <LoadingSpinner
			spinning />;
	} else if (search.length < MIN_SEARCH_QUERY_LENGTH) {
		emptyContent = null;
	}

	const handleOnChange = (entries: Array<LookAheadFilterOption> | undefined) => {
		onChange({
			[ id ]: {
				format,
				value: entries ?? []
			}
		} as DynamicFilter<Array<LookAheadFilterOption>>);
	};

	return (
		<FilterComponent
			className='look-ahead-filter'
			{ ...props }
			tagProps={ {
				id,
				value: valueWithNull?.map((item) => item.label).join(', ') ?? null
			} }
			value={ valueWithNull }>
			<Flex
				align='center'
				gap={ 8 }>
				<Select
					allowClear
					className='filter'
					filterOption={ false }
					id={ getFilterId(id) }
					labelInValue
					loading={ isLoading }
					maxTagCount='responsive'
					mode='multiple'
					notFoundContent={ emptyContent }
					onChange={ handleOnChange }
					onSearch={ getSelectOptions }
					options={ options }
					placeholder={ placeholder ?? PLACEHOLDERS_COPY.FILTER_BY(label) }
					popupRender={ (menu) => (isLoading ? <LoadingSpinner spinning /> : menu) }
					showSearch
					value={ valueWithNull } />
			</Flex>
		</FilterComponent>
	);
};
